import React, { useEffect, useMemo, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Megaphone, Plus, Paperclip, Trash2, Mail, X, Download, Check, CheckCheck, Pencil } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { MarkdownToolbar } from "@/pages/GuidanceWorkspace";

const TARGET_TYPES = [
  { value: "everyone", label: "Everyone" },
  { value: "person",   label: "Person(s)" },
  { value: "group",    label: "Group (team_section)" },
  { value: "unit",     label: "Org Unit(s)" },
  { value: "project",  label: "Project(s) / Workspace(s)" },
  { value: "role",     label: "Role(s)" },
];

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function AnnouncementsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showComposer, setShowComposer] = useState(false);
  const [editing, setEditing] = useState(null); // announcement being edited
  const [targets, setTargets] = useState({ can_publish: false, people: [], groups: [], units: [], projects: [] });

  const canPublish = useMemo(() => {
    const role = user?.role;
    if (!role) return false;
    if (role === "intern" || user?.is_intern) return false;
    return ["admin", "super_admin", "employee", "mentor", "tutor"].includes(role);
  }, [user]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/announcements");
      setItems(r.data.items || []);
    } catch {
      /* silent */
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!canPublish) return;
    api.get("/announcements/targets").then((r) => setTargets(r.data || {})).catch(() => {});
  }, [canPublish]);

  const markSeen = async (id) => {
    try {
      await api.post(`/announcements/${id}/seen`);
      setItems((prev) => prev.map((a) => a.id === id ? { ...a, seen: true, seen_by: [...(a.seen_by || []), user.user_id] } : a));
      window.dispatchEvent(new CustomEvent("announcements:refresh"));
    } catch { /* ignore */ }
  };

  const markAllSeen = async () => {
    try {
      await api.post("/announcements/mark-all-seen");
      setItems((prev) => prev.map((a) => ({ ...a, seen: true })));
      window.dispatchEvent(new CustomEvent("announcements:refresh"));
      toast.success("All caught up");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't mark all as seen");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this announcement for everyone?")) return;
    try {
      await api.delete(`/announcements/${id}`);
      setItems((prev) => prev.filter((a) => a.id !== id));
      toast.success("Announcement deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete announcement");
    }
  };

  return (
    <div className="px-6 lg:px-10 py-8 max-w-6xl mx-auto" data-testid="announcements-page">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.24em] text-kb-ink-muted flex items-center gap-2">
            <Megaphone className="w-3.5 h-3.5" /> ANNOUNCEMENTS
          </div>
          <h1 className="font-serif text-3xl text-kb-ink mt-1">Announcements</h1>
        </div>
        <div className="flex items-center gap-2">
          {items.some((a) => !a.seen) && (
            <button onClick={markAllSeen} className="kb-btn-secondary text-sm inline-flex items-center gap-1.5" data-testid="mark-all-seen-btn">
              <CheckCheck className="w-4 h-4" /> Mark all seen
            </button>
          )}
          {canPublish && (
            <button
              onClick={() => setShowComposer(true)}
              className="kb-btn-primary text-sm inline-flex items-center gap-1.5"
              data-testid="new-announcement-btn"
            >
              <Plus className="w-4 h-4" /> New announcement
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="kb-mono text-xs uppercase tracking-wider text-kb-ink-muted">Loading…</div>
      ) : items.length === 0 ? (
        <div className="kb-card p-10 text-center">
          <Megaphone className="w-8 h-8 mx-auto text-kb-ink-muted mb-3" />
          <div className="text-kb-ink font-medium">No announcements yet.</div>
          <div className="text-sm text-kb-ink-muted">You&apos;ll see the latest updates from your team here.</div>
        </div>
      ) : (
        <div className="space-y-8" data-testid="announcements-list">
          {(() => {
            // Group announcements by their `topic` bucket. Untopiced items
            // collapse under a single "General" heading so the layout stays
            // consistent regardless of how disciplined authors are with
            // tagging.
            const groups = {};
            for (const a of items) {
              const key = (a.topic || "General").trim() || "General";
              (groups[key] = groups[key] || []).push(a);
            }
            const keys = Object.keys(groups).sort((a, b) => (a === "General" ? 1 : b === "General" ? -1 : a.localeCompare(b)));
            return keys.map((k) => (
              <section key={k} data-testid={`ann-group-${k}`}>
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-primary mb-2">{k} · {groups[k].length}</div>
                <ul className="space-y-3">
                  {groups[k].map((a) => (
                    <AnnouncementCard
                      key={a.id}
                      ann={a}
                      currentUserId={user?.user_id}
                      onSeen={() => markSeen(a.id)}
                      onDelete={() => remove(a.id)}
                      onEdit={() => setEditing(a)}
                      canModify={a.author_user_id === user?.user_id || ["admin", "super_admin"].includes(user?.role)}
                    />
                  ))}
                </ul>
              </section>
            ));
          })()}
        </div>
      )}

      {showComposer && (
        <AnnouncementComposer
          targets={targets}
          onClose={() => setShowComposer(false)}
          onCreated={(doc) => {
            setShowComposer(false);
            setItems((prev) => [{ ...doc, seen: true }, ...prev]);
            window.dispatchEvent(new CustomEvent("announcements:refresh"));
            toast.success("Announcement published");
          }}
        />
      )}

      {editing && (
        <AnnouncementEditModal
          ann={editing}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setEditing(null);
            setItems((prev) => prev.map((a) => a.id === updated.id ? { ...a, ...updated } : a));
            toast.success("Announcement updated");
          }}
        />
      )}
    </div>
  );
}

function AnnouncementEditModal({ ann, onClose, onSaved }) {
  const [title, setTitle] = useState(ann.title || "");
  const [topic, setTopic] = useState(ann.topic || "");
  const [body, setBody] = useState(ann.body || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      const r = await api.put(`/announcements/${ann.id}`, {
        title: title.trim(), topic: topic.trim() || null, body,
      });
      onSaved?.(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="ann-edit-modal">
      <div className="bg-white rounded-sm max-w-2xl w-full shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h2 className="font-serif text-xl text-kb-ink">Edit announcement</h2>
          <button onClick={onClose} className="p-1.5 text-kb-ink-muted hover:text-kb-primary" data-testid="ann-edit-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className="kb-input mt-1 text-sm w-full" data-testid="ann-edit-title" />
          </div>
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Topic</label>
            <input value={topic} onChange={(e) => setTopic(e.target.value)} className="kb-input mt-1 text-sm w-full" data-testid="ann-edit-topic" />
          </div>
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Body (Markdown)</label>
            <textarea rows={8} value={body} onChange={(e) => setBody(e.target.value)} className="kb-input mt-1 text-sm w-full font-mono" data-testid="ann-edit-body" />
          </div>
        </div>
        <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="kb-btn-secondary text-sm">Cancel</button>
          <button onClick={save} disabled={busy} className="kb-btn-primary text-sm" data-testid="ann-edit-save">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AnnouncementCard({ ann, onSeen, onDelete, onEdit, canModify }) {
  const unseen = !ann.seen;
  const [expanded, setExpanded] = useState(false);
  // Auto-collapse anything longer than ~600 chars or 8+ line breaks so cards
  // stay skimmable — user opts in with a "Show more" link.
  const bodyLen = (ann.body || "").length;
  const bodyLines = (ann.body || "").split("\n").length;
  const isLong = bodyLen > 600 || bodyLines > 8;
  const shown = expanded || !isLong ? ann.body : ann.body.slice(0, 600);
  return (
    <li
      className={`kb-card p-5 border-l-4 ${unseen ? "border-l-kb-primary bg-kb-subtle/40" : "border-l-slate-200"}`}
      data-testid={`announcement-${ann.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-serif text-lg text-kb-ink truncate">{ann.title}</h3>
            {unseen && <span className="kb-mono text-[9px] uppercase tracking-wider bg-kb-primary text-white px-1.5 py-0.5 rounded-sm">NEW</span>}
            {ann.send_email && <Mail className="w-3.5 h-3.5 text-kb-ink-muted" title="Also sent via email" />}
            {ann.edited_at && <span className="kb-mono text-[9px] text-kb-ink-light">· edited</span>}
          </div>
          <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
            {ann.author_name} · {fmtDate(ann.created_at)} · target: {ann.target_type}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {unseen && (
            <button onClick={onSeen} className="p-1.5 text-kb-ink-muted hover:text-kb-primary" title="Mark as seen" data-testid={`ann-seen-${ann.id}`}>
              <Check className="w-4 h-4" />
            </button>
          )}
          {canModify && (
            <button onClick={onEdit} className="p-1.5 text-kb-ink-muted hover:text-kb-primary" title="Edit" data-testid={`ann-edit-${ann.id}`}>
              <Pencil className="w-4 h-4" />
            </button>
          )}
          {canModify && (
            <button onClick={onDelete} className="p-1.5 text-kb-ink-muted hover:text-rose-600" title="Delete" data-testid={`ann-delete-${ann.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      {ann.body && (
        <div className="mt-3 text-sm text-kb-ink kb-task-md leading-relaxed" data-testid={`ann-body-${ann.id}`}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{shown}</ReactMarkdown>
          {isLong && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary hover:underline mt-1"
              data-testid={`ann-toggle-${ann.id}`}
            >
              {expanded ? "Show less" : `Show more (${bodyLen - 600}+ chars)`}
            </button>
          )}
        </div>
      )}
      {ann.attachment?.url && (
        <div className="mt-3">
          {ann.attachment.kind === "image" ? (
            <a href={ann.attachment.url} download={ann.attachment.name || "image.png"} target="_blank" rel="noreferrer">
              <img src={ann.attachment.url} alt={ann.attachment.name} className="max-h-64 rounded-sm border border-slate-200" />
            </a>
          ) : (
            <a
              href={ann.attachment.url}
              download={ann.attachment.name || "attachment"}
              className="inline-flex items-center gap-2 kb-btn-secondary text-xs"
            >
              <Download className="w-3.5 h-3.5" /> {ann.attachment.name || "Download attachment"}
            </a>
          )}
        </div>
      )}
    </li>
  );
}

function AnnouncementComposer({ targets, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [topic, setTopic] = useState("");
  const [body, setBody] = useState("");
  const bodyRef = useRef(null);
  const [targetType, setTargetType] = useState("everyone");
  const [selected, setSelected] = useState([]);
  const [sendEmail, setSendEmail] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [query, setQuery] = useState("");

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (targetType === "person") {
      const arr = targets.people || [];
      return q ? arr.filter((p) => (p.name || "").toLowerCase().includes(q) || (p.email || "").toLowerCase().includes(q)) : arr;
    }
    if (targetType === "group") return (targets.groups || []).filter((g) => !q || g.toLowerCase().includes(q));
    if (targetType === "unit")  return (targets.units || []).filter((u) => !q || u.name.toLowerCase().includes(q));
    if (targetType === "project") return (targets.projects || []).filter((p) => !q || (p.label || "").toLowerCase().includes(q));
    if (targetType === "role") return (targets.roles || []).filter((r) => !q || r.toLowerCase().includes(q));
    return [];
  }, [targetType, targets, query]);

  const toggle = (v) => setSelected((prev) => prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]);

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/announcements/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachment(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Attachment upload failed");
    } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    if (targetType !== "everyone" && selected.length === 0) { toast.error("Pick at least one target"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/announcements", {
        title: title.trim(),
        topic: topic.trim() || null,
        body,
        target_type: targetType,
        target_ids: selected,
        attachment,
        send_email: sendEmail,
      });
      onCreated?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't publish announcement");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="announcement-composer">
      <div className="bg-white rounded-sm max-w-3xl w-full max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white">
          <h2 className="font-serif text-2xl text-kb-ink">New Announcement</h2>
          <button onClick={onClose} className="p-1.5 text-kb-ink-muted hover:text-kb-primary" data-testid="composer-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Title</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-sm text-sm"
                placeholder="Server maintenance this Friday…"
                data-testid="composer-title"
              />
            </div>
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Topic (for grouping)</label>
              <input
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-sm text-sm"
                placeholder="e.g. Ops, Hiring, Research"
                data-testid="composer-topic"
                list="composer-topic-suggestions"
              />
              <datalist id="composer-topic-suggestions">
                <option value="Ops" />
                <option value="Hiring" />
                <option value="Research" />
                <option value="Announcements" />
                <option value="Events" />
              </datalist>
            </div>
          </div>
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Body (Markdown supported)</label>
            <MarkdownToolbar textareaRef={bodyRef} value={body} onChange={setBody} />
            <textarea
              ref={bodyRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono"
              placeholder="Details… (Markdown: **bold**, # heading, - list, `code`)"
              data-testid="composer-body"
            />
            {body && (
              <div className="mt-2 border border-slate-200 rounded-sm p-3 bg-slate-50 text-sm kb-task-md" data-testid="composer-body-preview">
                <div className="kb-mono text-[9px] uppercase tracking-wider text-kb-ink-muted mb-1">PREVIEW</div>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
              </div>
            )}
          </div>

          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Target</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {TARGET_TYPES.map((t) => (
                <button
                  key={t.value}
                  onClick={() => { setTargetType(t.value); setSelected([]); }}
                  className={`px-3 py-1.5 text-xs rounded-sm border ${targetType === t.value ? "bg-kb-primary text-white border-kb-primary" : "bg-white text-kb-ink border-slate-300"}`}
                  data-testid={`target-${t.value}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {targetType !== "everyone" && (
            <div className="border border-slate-200 rounded-sm p-3">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-xs mb-2"
                data-testid="composer-search"
              />
              <div className="max-h-56 overflow-y-auto space-y-1" data-testid="composer-target-list">
                {list.length === 0 && <div className="text-xs text-kb-ink-muted">No matches.</div>}
                {list.map((v) => {
                  const val = targetType === "person" ? v.user_id : (targetType === "unit" || targetType === "project") ? v.id : v;
                  const label = targetType === "person" ? `${v.name} — ${v.email}` : (targetType === "unit" ? v.name : (targetType === "project" ? v.label : v));
                  const checked = selected.includes(val);
                  return (
                    <label key={val} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded-sm">
                      <input type="checkbox" checked={checked} onChange={() => toggle(val)} />
                      <span className="truncate">{label}</span>
                    </label>
                  );
                })}
              </div>
              {selected.length > 0 && (
                <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary mt-2">
                  {selected.length} selected
                </div>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-4">
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <Paperclip className="w-4 h-4 text-kb-ink-muted" />
              <input
                type="file"
                onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])}
                className="hidden"
                data-testid="composer-file-input"
                id="composer-file-input"
              />
              <span className="kb-btn-secondary text-xs" onClick={() => document.getElementById("composer-file-input")?.click()}>
                {uploading ? "Uploading…" : (attachment ? `Attached: ${attachment.name}` : "Attach file")}
              </span>
              {attachment && (
                <button onClick={() => setAttachment(null)} className="text-xs text-rose-600 hover:underline">remove</button>
              )}
            </label>
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer" data-testid="composer-email-toggle">
              <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
              <Mail className="w-4 h-4 text-kb-ink-muted" />
              Also send via email
            </label>
          </div>
        </div>
        <div className="p-5 border-t border-slate-200 flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="kb-btn-secondary text-sm">Cancel</button>
          <button
            onClick={submit}
            disabled={submitting}
            className="kb-btn-primary text-sm disabled:opacity-50"
            data-testid="composer-publish-btn"
          >
            {submitting ? "Publishing…" : "Publish announcement"}
          </button>
        </div>
      </div>
    </div>
  );
}
